import React from 'react';
import { Link } from 'react-router';
import { AlertTriangle, Ban, CheckCircle2, Circle, Lock, MinusCircle } from 'lucide-react';
import type { SetupWizardStep, SetupWizardStepStatus } from '../leagueSetupWizard';

interface LeagueSetupStepCardProps {
  step: SetupWizardStep;
}

function StatusIcon({ status }: { status: SetupWizardStepStatus }) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" aria-hidden />;
    case 'needs_action':
      return <Ban className="h-5 w-5 text-red-400 shrink-0 mt-0.5" aria-hidden />;
    case 'locked':
      return <Lock className="h-5 w-5 text-secondary-400 shrink-0 mt-0.5" aria-hidden />;
    case 'optional':
      return <MinusCircle className="h-5 w-5 text-secondary-500 shrink-0 mt-0.5" aria-hidden />;
    default:
      return <Circle className="h-5 w-5 text-secondary-500 shrink-0 mt-0.5" aria-hidden />;
  }
}

const statusLabel: Record<SetupWizardStepStatus, string> = {
  complete: 'Complete',
  needs_action: 'Needs action',
  warning: 'Needs attention',
  locked: 'Owner managed',
  optional: 'Optional',
};

const LeagueSetupStepCard: React.FC<LeagueSetupStepCardProps> = ({ step }) => {
  return (
    <article
      className="card space-y-3 overflow-hidden"
      data-testid={`setup-wizard-step-${step.id}`}
      data-setup-reason={step.reasonCode}
    >
      <div className="flex items-start gap-3">
        <StatusIcon status={step.status} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-white">{step.title}</h3>
            <span className="text-xs rounded-full border border-secondary-700 px-2 py-0.5 text-secondary-400">
              {statusLabel[step.status]}
            </span>
          </div>
          <p className="text-sm text-secondary-400">{step.description}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:pl-8">
        {step.primaryActionHref && step.primaryActionLabel ? (
          <Link
            to={step.primaryActionHref}
            className="btn-secondary inline-flex min-h-11 items-center justify-center text-sm"
            data-testid={`setup-wizard-step-${step.id}-cta`}
          >
            {step.primaryActionLabel}
          </Link>
        ) : null}
        {step.secondaryActionHref && step.secondaryActionLabel ? (
          <Link
            to={step.secondaryActionHref}
            className="btn-secondary inline-flex min-h-11 items-center justify-center text-sm"
            data-testid={`setup-wizard-step-${step.id}-secondary-cta`}
          >
            {step.secondaryActionLabel}
          </Link>
        ) : null}
      </div>
    </article>
  );
};

export default LeagueSetupStepCard;
